import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Grid, Typography, Stack, Box } from '@mui/material';
import {
  AutoAwesome,
  Cancel,
  Chat,
  CheckCircleOutline,
  Refresh,
  Title,
  Warning,
} from '@mui/icons-material';
import { Loader } from '@prismamedia/one-components';
import { useStyles } from './styles';
import { TextSuggestionMenu } from './TextSuggestionMenu/index';
import { BrandKey } from '../../__generated__/queries-web';
import { useOpenAiPrompts } from '../../utils/useOpenAiPrompts';
import { callOpenAI } from '../../utils/openAI';
import ReactGA from 'react-ga4';

interface TextSuggestionProps {
  anchorRef: HTMLDivElement | null;
  brandKey: BrandKey;
  className?: string;
  handleClose: () => void;
  fieldValue: string | null;
  title: string;
  body?: string;
  lead?: string;
  onConfirm: (suggestion: string) => void;
}

type MenuKeys = 'new' | 'edit' | 'confirm';

export interface MenuItem {
  icon: () => ReactNode;
  label: string;
  onClick: () => void;
  subMenuItems?: MenuItem[];
}

export type Menu = Record<MenuKeys, MenuItem[]>;

export const TextSuggestion: FC<TextSuggestionProps> = ({
  anchorRef,
  brandKey,
  handleClose,
  fieldValue,
  title,
  body,
  lead,
  onConfirm,
}) => {
  const classes = useStyles();
  const [isDisplay, setIsDisplay] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [menuKey, setMenuKey] = useState<'new' | 'edit' | 'confirm'>(
    fieldValue ? 'edit' : 'new',
  );
  const [suggestion, setSuggestion] = useState<string>('');
  const [menuRef, setMenuRef] = useState(anchorRef);
  const promptRef = useRef(null);
  // CREATE OPTIONS FROM FETCHED PROMPTS
  const prompts = useOpenAiPrompts({ brandKey, body, lead, title });
  const retryPrompt = prompts.filter((prompt) => prompt.retry);
  const retryOptions = retryPrompt.map((prompt) => ({
    icon: () => <Chat />,
    label: prompt.label,
    onClick: async () => {
      setIsDisplay(true);
      const value = fieldValue || '';
      const promptBody = prompt.body.includes('${currentField}')
        ? prompt.body.replace(/\${currentField}/g, value)
        : `${prompt.body} ${value}`;
      const result = await handleOpenAi(promptBody);
      setMenuKey('confirm');
      setSuggestion(result);
    },
  }));
  const generateOptions = prompts
    .filter((prompt) => !prompt.retry)
    .map((prompt) => ({
      icon: () => <Title className={classes.icon} />,
      label: prompt.label,
      onClick: () => onGenerate(`${prompt.body}`),
    }));
  const handleOpenAi = async (content: string) => {
    setIsLoading(true);
    const openAiResponse = await callOpenAI(content);
    setIsLoading(false);
    return openAiResponse;
  };

  const onGenerate = useCallback(async (content: string) => {
    setIsDisplay(true);
    ReactGA.event({
      category: 'Article IA Titre accroche',
      action: 'Article IA Generation',
      label: window.location.href,
    });
    const result = await handleOpenAi(content);
    setMenuKey('confirm');
    setSuggestion(result);
  }, []);

  const menu: Menu = useMemo(
    () => ({
      new: generateOptions,
      edit: [...generateOptions, ...retryOptions],
      confirm: [
        {
          icon: () => <CheckCircleOutline />,
          label: 'Valider',
          onClick: () => {
            setIsDisplay(false);
            onConfirm(suggestion);
            handleClose();
          },
        },
        {
          icon: () => <Refresh />,
          label: 'Réessayer',
          onClick: async () => {
            const result = await handleOpenAi(
              `${retryPrompt[0].body} ${suggestion}`,
            );
            setSuggestion(result);
          },
        },
        {
          icon: () => <Cancel />,
          label: 'Annuler',
          onClick: () => {
            handleClose();
            setIsDisplay(false);
          },
        },
      ],
    }),
    [
      suggestion,
      handleClose,
      onConfirm,
      retryPrompt,
      retryOptions,
      generateOptions,
    ],
  );

  useEffect(() => {
    isDisplay ? setMenuRef(promptRef.current) : setMenuRef(anchorRef);
  }, [isDisplay, anchorRef]);

  return (
    <>
      {isDisplay && (
        <Grid container className={classes.wrapper} ref={promptRef}>
          <Grid item xs={12} pt={1}>
            <Stack direction="row" alignItems="center" px={1} py={0.5}>
              <AutoAwesome className={classes.icon} />
              {isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    paddingRight: '8px',
                  }}
                >
                  <Loader color="warning" thickness={5} size={20} />
                </Box>
              ) : (
                <Typography
                  color="inherit"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                  ml={1.5}
                >
                  {suggestion}
                </Typography>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              className={classes.helper}
              px={1.5}
              py={0.75}
            >
              <Warning className={classes.smallIcon} />
              <Typography color="inherit" variant="caption" ml={1}>
                Les textes générés par l'IA peuvent être inexacts ou trompeurs.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      )}
      {!isLoading && (
        <TextSuggestionMenu
          anchorRef={menuRef}
          handleClose={handleClose}
          menuItems={menu}
          menuKey={menuKey}
        />
      )}
    </>
  );
};
